'use strict'

const {
  getCurrentPageSize,
  getPaginationData,
  selectCurrentRecordIndex,
  selectDefaultDraft,
  isWriteOnly,
  getNumberOfPagesToShow
} = require('../dataset-controller/rootReducer')
const actionTypes = require('./actionTypes')
const queue = require('../actions-queue/addToQueue')
const initAdapters = require('../adapters/initAdapters')
const queryResults = require('../helpers/queryResults')

const setCurrentIndex = (index, suppressRefreshView = false) => ({
  type: actionTypes.GO_TO_INDEX,
  index,
  suppressRefreshView
})

const refreshCurrentRecord = () => ({
  type: actionTypes.REFRESH_CURRENT_RECORD
})

const refreshCurrentView = () => ({
  type: actionTypes.REFRESH_CURRENT_VIEW
})

const setCurrentRecord = (record, recordIndex) => ({
  type: actionTypes.SET_CURRENT_RECORD,
  record,
  recordIndex
})

const updateFields = (fieldsToUpdate, updateSource) => ({
  type: actionTypes.UPDATE_FIELDS,
  fieldsToUpdate,
  updateSource
})

const incrementNumOfPagesToShow = () => ({
  type: actionTypes.INCREMENT_NUM_PAGES_TO_SHOW
})

const nextPage = () => ({
  type: actionTypes.GO_TO_NEXT_PAGE
})

const previousPage = () => ({
  type: actionTypes.GO_TO_PREVIOUS_PAGE
})

const loadPage = pageNumber => ({
  type: actionTypes.LOAD_PAGE,
  pageNumber
})

const revert = () => ({
  type: actionTypes.REVERT_CHANGES
})

const flushDraft = () => ({
  type: actionTypes.SAVE_RECORD
})

const setDefaultRecord = record => ({
  type: actionTypes.SET_DEFAULT_RECORD,
  record
})

const newRecord = atIndex => ({
  type: actionTypes.NEW_RECORD,
  atIndex
})

const remove = () => ({
  type: actionTypes.REMOVE_CURRENT_RECORD
})

const doFetch = async (recordStore, fromIndex, length, byRefField) => {
  const fetchedItems = await recordStore(byRefField).matchWith({
    Nothing: () => queryResults.Empty(),
    Just: async ({ value: service }) => {
      const queryResult = await service.getRecords(fromIndex, length)

      return queryResult
    }
  })

  return fetchedItems.get()
}

const fetchCurrentPage = (recordStore, state) => {
  const pageSize = getCurrentPageSize(state) * getNumberOfPagesToShow(state)
  const { offset } = getPaginationData(state)

  return doFetch(recordStore, offset, pageSize)
}

const updateComponentsBasedOnRecord = adapterApi => (
  componentIdToExcludeFromUpdatingComponentsBasedOnRecord,
  updatedFields
) => {
  adapterApi().currentRecordModified(
    updatedFields,
    componentIdToExcludeFromUpdatingComponentsBasedOnRecord
  )
}
exports.updateComponentsBasedOnRecord = updateComponentsBasedOnRecord

exports.fetchInitialDataAndSetInitialCurrentRecord = (
  hasPrefetchedData,
  errorReporter
) => {
  if (hasPrefetchedData) {
    return setInitialCurrentRecord
  }

  return queue(fetchInitialDataAndSetInitialCurrentRecord(errorReporter))
}

const setInitialCurrentRecord = (dispatch, getState, { recordStore }) =>
  recordStore().matchWith({
    Nothing: () => Promise.resolve(),
    Just: ({ value: service }) =>
      service
        .getSeedRecords()
        .chain(({ items }) => dispatch(setCurrentRecord(items[0], 0)))
  })

const fetchInitialDataAndSetInitialCurrentRecord = errorReporter => (
  dispatch,
  getState,
  { recordStore, fedopsLogger }
) => {
  const { timeoutId } = fedopsLogger.interactionStarted(
    'dataset/pageReady/getData'
  )
  return recordStore().matchWith({
    Nothing: () => Promise.resolve(),
    Just: ({ value: service }) =>
      service
        .seed()
        .then(() =>
          service
            .getRecords(0, 1)
            .then(queryResult =>
              queryResult.chain(({ items }) =>
                dispatch(setCurrentRecord(items[0], 0))
              )
            )
            .then(() =>
              fedopsLogger.interactionEnded('dataset/pageReady/getData', {
                timeoutId
              })
            )
        )
        .catch(e => errorReporter('Failed to load initial data', e))
  })
}

exports.initWriteOnly = isScoped =>
  queue(async function(dispatch, getState, { recordStore }) {
    await recordStore().matchWith({
      Nothing: () => {
        throw new Error('Record store service not ready yet')
      },
      Just: async ({ value: service }) => {
        if (!isScoped) {
          service.newRecord(0, selectDefaultDraft(getState()))
        }
        await dispatch(setCurrentIndex(0))
      }
    })
  })

exports.reInitWriteOnly = () =>
  queue(async function(dispatch, getState) {
    dispatch(newRecord(0))
  })

const initAdaptersThunk = (dispatch, getState, { adapterApi }) => {
  return initAdapters(adapterApi)
}

exports.initAdapters = hasPrefetchedData =>
  hasPrefetchedData ? initAdaptersThunk : queue(initAdaptersThunk)

exports.refresh = () =>
  queue(async function(
    dispatch,
    getState,
    { adapterApi, eventListeners: { fireEvent }, recordStore }
  ) {
    await recordStore().chain(async service => {
      service.resetDraft(
        selectCurrentRecordIndex(getState()),
        selectDefaultDraft(getState())
      )
      service.reset()

      if (isWriteOnly(getState())) {
        service.newRecord(0, selectDefaultDraft(getState()))
      }
      await dispatch(setCurrentIndex(0, true))

      await Promise.all(adapterApi().recordSetLoaded())

      fireEvent('currentIndexChanged', 0)
    })
  })

exports.fetchCurrentPage = fetchCurrentPage
exports.doFetch = doFetch
exports.flushDraft = flushDraft
exports.incrementNumOfPagesToShow = incrementNumOfPagesToShow
exports.nextPage = nextPage
exports.previousPage = previousPage
exports.loadPage = loadPage
exports.refreshCurrentRecord = refreshCurrentRecord
exports.refreshCurrentView = refreshCurrentView
exports.revert = revert
exports.setCurrentIndex = setCurrentIndex
exports.setCurrentRecord = setCurrentRecord
exports.setDefaultRecord = setDefaultRecord
exports.updateFields = updateFields
exports.remove = remove
exports.newRecord = newRecord



// WEBPACK FOOTER //
// ./records/actions.js